import { useAuth } from './useAuth';
import { useNavigate } from 'react-router-dom';
import { useAlert } from './useAlert';

const TIER_LIMITS = {
  free: {
    transcriptions: Infinity,
    name: 'Free'
  },
  basic: {
    transcriptions: Infinity,
    name: 'Basic'
  },
  premium: {
    transcriptions: Infinity,
    name: 'Premium'
  }
};

export function useTierLimits() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { showConfirm } = useAlert();

  const currentTier = user?.tier || 'free';
  const limits = TIER_LIMITS[currentTier];

  const checkTranscriptionLimit = (currentUsage = 0) => {
    if (currentUsage >= limits.transcriptions) {
      return {
        allowed: false,
        message: `You've reached your ${limits.name} plan limit of ${limits.transcriptions === Infinity ? 'unlimited' : limits.transcriptions} transcriptions per month.`,
        upgradeMessage: `Upgrade to ${currentTier === 'free' ? 'Basic' : 'Premium'} plan for ${currentTier === 'free' ? '100 transcriptions' : 'unlimited transcriptions'} per month.`
      };
    }

    return {
      allowed: true,
      remaining: limits.transcriptions === Infinity ? Infinity : limits.transcriptions - currentUsage
    };
  };

  const promptUpgrade = async (reason, upgradeMessage) => {
    const confirmed = await showConfirm(
      'Upgrade Required',
      `${reason}\n\n${upgradeMessage}\n\nWould you like to upgrade now?`,
      'Upgrade',
      'Cancel'
    );

    if (confirmed) {
      navigate('/payment-setup');
    }

    return confirmed;
  };

  const getNextTier = () => {
    switch (currentTier) {
      case 'free':
        return 'basic';
      case 'basic':
        return 'premium';
      default:
        return null;
    }
  };

  const getNextTierName = () => {
    const nextTier = getNextTier();
    return nextTier ? TIER_LIMITS[nextTier].name : null;
  };

  return {
    currentTier,
    limits,
    checkTranscriptionLimit,
    promptUpgrade,
    getNextTier,
    getNextTierName,
    canUpgrade: getNextTier() !== null
  };
}
